import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Eraser,
  Hand,
  Highlighter,
  Pen,
  StickyNote,
  Trash2,
  Type,
  Wand2,
  X,
} from "lucide-react";

import {
  ANNOTATION_COLORS,
  ANNOTATION_COLOR_HEX,
  BRUSH_SIZES,
  type AnnotationTool,
  useAnnotationStore,
} from "@/stores/annotation-store";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface AnnotationToolbarProps {
  // Used so the Clear-current-slide button knows which slide to wipe in
  // the per-slide store. Parent passes the live currentSlideIndex.
  slideIndex: number;
  // Optional className for positioning by the parent.
  className?: string;
  // Optional clear handler — parents that need to BC-broadcast the clear
  // to peer surfaces provide this; otherwise we just mutate the local
  // store via clearSlide(slideIndex).
  onClear?: () => void;
  // Prev / Next slide handlers. When provided, the toolbar renders inline
  // navigation buttons.
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
}

// Tool palette. Order = left-to-right in the expanded toolbar.
//   - Marker / Highlighter / Eraser: persistent strokes via drauu.
//   - Laser: same draw mode + per-stroke fade.
//   - Arrow: drauu line mode + arrowEnd brush option.
//   - Text / Note: custom — drauu's pointerdown is intercepted, an HTML
//     input opens at the click point, blur/Enter commits as an SVG
//     element appended to drauu.el. See annotation-overlay.tsx.
const TOOLS: Array<{
  id: Exclude<AnnotationTool, null>;
  icon: typeof Pen;
  label: string;
}> = [
  { id: "hand", icon: Hand, label: "Move (Hand)" },
  { id: "draw", icon: Pen, label: "Marker" },
  { id: "highlight", icon: Highlighter, label: "Highlighter" },
  { id: "arrow", icon: ArrowUpRight, label: "Arrow" },
  { id: "text", icon: Type, label: "Text" },
  { id: "note", icon: StickyNote, label: "Sticky note" },
  { id: "eraser", icon: Eraser, label: "Eraser" },
  { id: "laser", icon: Wand2, label: "Laser (auto-fade)" },
];

export function AnnotationToolbar({
  slideIndex,
  className,
  onClear,
  onPrev,
  onNext,
  canPrev = true,
  canNext = true,
}: AnnotationToolbarProps) {
  const activeTool = useAnnotationStore((s) => s.activeTool);
  const color = useAnnotationStore((s) => s.color);
  const brushSize = useAnnotationStore((s) => s.brushSize);
  const setTool = useAnnotationStore((s) => s.setTool);
  const setColor = useAnnotationStore((s) => s.setColor);
  const setBrushSize = useAnnotationStore((s) => s.setBrushSize);
  const clearSlide = useAnnotationStore((s) => s.clearSlide);

  // Collapsed state: just a single Pen trigger button. Clicking it opens
  // the toolbar with the marker tool pre-selected (Canva default).
  if (activeTool === null) {
    return (
      <button
        type="button"
        onClick={() => setTool("draw")}
        className={cn(
          "rounded-full bg-white/95 backdrop-blur shadow-md shadow-slate-900/10 ring-1 ring-slate-200 p-2.5 hover:bg-white transition-colors",
          className,
        )}
        aria-label="Open annotation toolbar"
        title="Annotate"
      >
        <Pen className="w-4 h-4 text-slate-700" />
      </button>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-full bg-white/95 backdrop-blur shadow-md shadow-slate-900/10 ring-1 ring-slate-200 px-2 py-1.5",
        className,
      )}
      role="toolbar"
      aria-label="Annotation tools"
    >
      {/* Inline slide navigation — only shown when parent provides
          handlers (presenter). The mirror's own chevron buttons are
          obscured by the overlay's pointer-events: auto while drawing,
          so the toolbar takes over navigation. */}
      {(onPrev || onNext) && (
        <>
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              disabled={!canPrev}
              aria-label="Previous slide"
              title="Previous slide"
              className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              disabled={!canNext}
              aria-label="Next slide"
              title="Next slide"
              className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          <div className="w-px h-6 bg-slate-200 mx-1" aria-hidden />
        </>
      )}

      {TOOLS.map((t) => {
        const Icon = t.icon;
        const isActive = activeTool === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setTool(t.id)}
            aria-label={t.label}
            aria-pressed={isActive}
            title={t.label}
            className={cn(
              "p-2 rounded-full transition-colors",
              isActive
                ? "bg-slate-900 text-white"
                : "text-slate-700 hover:bg-slate-100",
            )}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}

      <div className="w-px h-6 bg-slate-200 mx-1" aria-hidden />

      {/* Color popover — single swatch button shows current color; click
          to open a horizontal palette. Avoids the toolbar swelling to
          fit 5+ static swatches. */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Color (${color})`}
            title={`Color: ${color}`}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
          >
            <span
              className="block w-5 h-5 rounded-full ring-1 ring-slate-300"
              style={{ backgroundColor: ANNOTATION_COLOR_HEX[color] }}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="bottom"
          sideOffset={8}
          className="w-auto p-1.5 rounded-full flex items-center gap-1"
        >
          {ANNOTATION_COLORS.map((c) => {
            const isActive = color === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`Color ${c}`}
                aria-pressed={isActive}
                title={c}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center transition-transform",
                  isActive
                    ? "scale-110 ring-2 ring-slate-900 ring-offset-1"
                    : "hover:scale-105",
                )}
              >
                <span
                  className="block w-5 h-5 rounded-full ring-1 ring-slate-300"
                  style={{ backgroundColor: ANNOTATION_COLOR_HEX[c] }}
                />
              </button>
            );
          })}
        </PopoverContent>
      </Popover>

      {/* Size popover — single label button shows current size letter;
          click to open S/M/L/XL row. Same compaction rationale as color. */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Brush size (${brushSize})`}
            title={`Size: ${brushSize}`}
            className="min-w-[2rem] h-8 px-2 rounded-full text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
          >
            {brushSize}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="bottom"
          sideOffset={8}
          className="w-auto p-1.5 rounded-full flex items-center gap-1"
        >
          {BRUSH_SIZES.map((s) => {
            const isActive = brushSize === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setBrushSize(s)}
                aria-label={`Brush size ${s}`}
                aria-pressed={isActive}
                title={`Size ${s}`}
                className={cn(
                  "min-w-[2rem] h-8 px-2 rounded-full text-xs font-semibold transition-colors",
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100",
                )}
              >
                {s}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>

      <div className="w-px h-6 bg-slate-200 mx-1" aria-hidden />

      <button
        type="button"
        onClick={() => (onClear ? onClear() : clearSlide(slideIndex))}
        aria-label="Clear annotations on this slide"
        title="Clear this slide"
        className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => setTool(null)}
        aria-label="Close annotation toolbar"
        title="Close"
        className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
